import { Link } from "react-router-dom";
import { useEffect } from "react";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import Footer from "../components/Footer";
import { Building2, Key, Search, Send, Users, History } from "lucide-react";
import PublicNav from "../components/PublicNav";

function CodeBlock({ code }) {
  return (
    <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
      <pre className="text-sm text-green-400 font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-16">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function ArenaApiDocs() {
  useEffect(() => {
    document.title = "Joborigo: Arena API Documentation";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav sticky />

      <div className="flex-1 flex min-w-0">
        {/* Sidebar */}
        <aside className="hidden lg:block w-56 flex-shrink-0 border-r border-gray-100 dark:border-white/10 px-6 py-10 sticky top-16 self-start">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
            On this page
          </p>
          <nav className="flex flex-col gap-2">
            {[
              { href: "#overview", label: "Overview" },
              { href: "#prerequisites", label: "Prerequisites" },
              { href: "#authentication", label: "Authentication" },
              { href: "#browse", label: "Browse candidates" },
              { href: "#resume", label: "View resume" },
              { href: "#send-position", label: "Send job position" },
              { href: "#sent-positions", label: "Sent positions" },
              { href: "#connected", label: "Connected candidates" },
              { href: "#history", label: "Process history" },
              { href: "#errors", label: "Errors" },
              { href: "#rate-limits", label: "Rate limits" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="mt-8 pt-6 border-t border-gray-100 dark:border-white/10">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
              Also available
            </p>
            <Link
              to="/employers/api"
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition block"
            >
              ATS / Status push API →
            </Link>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 max-w-3xl min-w-0 px-6 lg:px-12 py-16">
          <div className="mb-12">
            <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
              Arena API
            </p>
            <h1 className="text-4xl font-extrabold mb-4">
              Arena API Documentation
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
              The Joborigo Arena API lets employers and agencies browse
              anonymous candidates, send job positions, and manage hiring
              processes, all without exposing candidate identities until they
              choose to engage.
            </p>
            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-xl">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <span className="font-semibold">Looking for status push?</span>{" "}
                The ATS integration and status push API is documented separately
                at{" "}
                <Link
                  to="/employers/api"
                  className="underline hover:text-amber-600"
                >
                  joborigo.com/employers/api
                </Link>
                .
              </p>
            </div>
          </div>

          <Section id="overview" title="Overview">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              The Arena is Joborigo's anonymous candidate marketplace.
              Candidates opt in and are visible only by their skills,
              availability, and preferences. No name, no gender, no photo. Joborigo never reveals a candidate's identity or contact details through the Arena API. Candidates write their own headline and summary, so anything they choose to put there is visible to you.            </p>
            <ol className="flex flex-col gap-3 mb-6">
              {[
                "Register an employer or agency account and generate an API key",
                "Browse anonymous candidates using filters",
                "Send a job position to a candidate. They receive it in their Joborigo inbox",
                "Track sent positions and candidate responses",
                "View full process history for connected candidates",
              ].map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-gray-500 dark:text-gray-400"
                >
                  <span className="w-6 h-6 rounded-full bg-brand-violet text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </Section>

          <Section id="prerequisites" title="Prerequisites">
            <div className="flex flex-col gap-3 mb-6">
              {[
                {
                  icon: Building2,
                  title: "Joborigo employer or agency account",
                  desc: "Register at joborigo.com/employers/register. Agencies should enable the agency toggle during registration.",
                },
                {
                  icon: Key,
                  title: "API key",
                  desc: "Generate one from your Employer Dashboard under API Keys. Never expose it in client-side code or public repositories.",
                },
                {
                  icon: Users,
                  title: "Candidates opted in to the Arena",
                  desc: "Candidates must have an active arena profile. Only those who have explicitly opted in are visible.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <div className="w-8 h-8 rounded-lg bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-5 py-4">
              <p className="text-sm text-brand-violet dark:text-brand-violet-on-dark font-medium mb-1">
                Base URL
              </p>
              <code className="text-sm font-mono text-gray-700 dark:text-gray-300">
                https://api.joborigo.com/api/v1
              </code>
            </div>
          </Section>

          <Section id="authentication" title="Authentication">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              All Arena API endpoints require authentication via an API key in
              the{" "}
              <code className="bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono">
                X-API-Key
              </code>{" "}
              header.
            </p>
            <CodeBlock
              code={`curl https://api.joborigo.com/api/v1/arena/candidates \\
  -H "X-API-Key: jbr_your_api_key_here"`}
            />
          </Section>

          <Section id="browse" title="Browse candidates">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Returns a list of anonymous candidates who have opted into the
              All candidates are fully anonymous. No names, emails, or
              identifying information is returned.
              All candidates are anonymous. Joborigo does not include names, emails, or contact details in any Arena API response.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-blue-600 px-2 py-1 rounded">
                GET
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                /arena/candidates
              </code>
            </div>

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Query parameters
            </p>
            <div className="flex flex-col gap-2 mb-5">
              {[
                {
                  param: "skill",
                  desc: "Filter by skill keyword, for example React or Python",
                },
                {
                  param: "availability",
                  desc: "immediate | one_month | three_months",
                },
                {
                  param: "location",
                  desc: "Location preference keyword, for example Nordics or Remote",
                },
                {
                  param: "salary_max",
                  desc: "Maximum salary expectation (integer)",
                },
                {
                  param: "open_to_relocation",
                  desc: "true to show only candidates open to relocation",
                },
              ].map((p) => (
                <div
                  key={p.param}
                  className="flex items-start gap-4 p-3 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <code className="text-xs font-mono text-brand-violet dark:text-brand-violet-on-dark w-40 flex-shrink-0">
                    {p.param}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {p.desc}
                  </span>
                </div>
              ))}
            </div>

            <CodeBlock
              code={`curl "https://api.joborigo.com/api/v1/arena/candidates?skill=React&availability=immediate" \\
  -H "X-API-Key: jbr_your_api_key_here"`}
            />

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`[
  {
    "id": "uuid",                          // arena profile ID
    "anonymous_handle": "React Dev · Stockholm",
    "location_preference": "Nordics",
    "availability": "immediate",
    "open_to_relocation": true,
    "salary_min": 60000,
    "salary_max": 80000,
    "salary_currency": "EUR",
    "skills_snapshot": [
      { "skill": "React", "years": 5 },
      { "skill": "TypeScript", "years": 3 }
    ]
  }
]`}
            />
          </Section>

          <Section id="resume" title="View candidate resume">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Returns the public resume for a candidate arena profile. Only
              available if the candidate has published a resume. No identifying
              information is included unless the candidate has chosen to make it
              public.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-blue-600 px-2 py-1 rounded">
                GET
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                {"/arena/candidates/{profile_id}/resume"}
              </code>
            </div>
            <CodeBlock
              code={`curl https://api.joborigo.com/api/v1/arena/candidates/uuid-here/resume \\
  -H "X-API-Key: jbr_your_api_key_here"`}
            />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`{
  "has_resume": true,
  "name": "Resume name",
  "summary": "Candidate summary...",
  "entries": [
    {
      "id": "uuid",
      "entry_type": "experience",
      "title": "Senior Frontend Developer",
      "organisation": "Tech Company",
      "location": "Stockholm",
      "started_at": "2021-01-01T00:00:00Z",
      "ended_at": null,
      "is_current": true,
      "description": "Led frontend development..."
    }
  ]
}`}
            />
          </Section>

          <Section id="send-position" title="Send a job position">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Send a job position to a candidate. The candidate receives it in
              their Joborigo inbox and can choose to import it as an
              application, dismiss it, or ignore it. No email address is exposed
              in this flow.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-green-600 px-2 py-1 rounded">
                POST
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                /arena/job-positions
              </code>
            </div>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Request body
            </p>
            <CodeBlock
              code={`{
  "arena_profile_id": "uuid",             // required — from browse candidates
  "title": "Senior Frontend Engineer",    // required
  "message": "Hi, we think you'd be a great fit...", // optional — shown first
  "description": "Join our team...",      // optional
  "location": "Stockholm, Sweden",        // optional
  "salary_min": 70000,                    // optional
  "salary_max": 90000,                    // optional
  "salary_currency": "EUR",              // default EUR
  "is_remote": true,                      // optional
  "process_steps": [                      // optional — shown as pipeline to candidate
    "Phone screen",
    "Technical assessment",
    "Final interview",
    "Offer"
  ]
}`}
            />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`{
  "id": "uuid",
  "title": "Senior Frontend Engineer",
  "created_at": "2026-04-26T10:00:00Z"
}`}
            />
          </Section>

          <Section id="sent-positions" title="Sent positions">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Returns all job positions you have sent to candidates, including
              whether each position has been imported as an application by the
              candidate.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-blue-600 px-2 py-1 rounded">
                GET
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                /arena/sent-positions
              </code>
            </div>
            <CodeBlock
              code={`curl https://api.joborigo.com/api/v1/arena/sent-positions \\
  -H "X-API-Key: jbr_your_api_key_here"`}
            />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`[
  {
    "id": "uuid",
    "title": "Senior Frontend Engineer",
    "location": "Stockholm, Sweden",
    "salary_min": 70000,
    "salary_max": 90000,
    "salary_currency": "EUR",
    "is_remote": true,
    "is_imported": true,   // true = candidate accepted and imported as application
    "created_at": "2026-04-26T10:00:00Z"
  }
]`}
            />
          </Section>

          <Section id="connected" title="Connected candidates">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Returns all candidates who have connected to your account, either
              via the Arena or via a connect token. Includes current application
              status and push history.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-blue-600 px-2 py-1 rounded">
                GET
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                /employer/connected-candidates
              </code>
            </div>
            <CodeBlock
              code={`curl https://api.joborigo.com/api/v1/employer/connected-candidates \\
  -H "X-API-Key: jbr_your_api_key_here"`}
            />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`[
  {
    "integration_id": "uuid",
    "application_id": "uuid",
    "candidate_ref": "your-internal-id",
    "company": "Spotify",
    "job_title": "Senior Frontend Developer",
    "current_status": "Interview",
    "last_push_status": "Interview",
    "last_push_at": "2026-04-25T10:00:00Z",
    "push_count": 3,
    "connected_at": "2026-04-01T10:00:00Z"
  }
]`}
            />
          </Section>

          <Section id="history" title="Process history">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Returns the full status and correspondence history for a connected
              candidate, including their responses to meetings, document
              requests, and assessments.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-blue-600 px-2 py-1 rounded">
                GET
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                {"/employer/connected-candidates/{application_id}/history"}
              </code>
            </div>
            <CodeBlock
              code={`curl https://api.joborigo.com/api/v1/employer/connected-candidates/uuid-here/history \\
  -H "X-API-Key: jbr_your_api_key_here"`}
            />
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`{
  "statuses": [
    {
      "id": "uuid",
      "status": "Interview",
      "note": "Scheduled for Monday",
      "source": "employer",
      "created_at": "2026-04-25T10:00:00Z",
      "actions": [
        {
          "id": "uuid",
          "action_type": "meeting",
          "title": "First round interview",
          "description": "Technical interview",
          "meeting_at": "2026-05-05T10:00:00Z",
          "duration_minutes": 45,
          "attendees": ["Jane Smith (HR)"],
          "response": "accepted",   // candidate response
          "created_at": "2026-04-25T10:00:00Z"
        }
      ]
    }
  ]
}`}
            />
          </Section>

          <Section id="errors" title="Errors">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              All errors return JSON with a{" "}
              <code className="bg-gray-100 dark:bg-white/10 px-1 rounded text-xs font-mono">
                detail
              </code>{" "}
              field.
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  code: "400",
                  meaning: "Bad request: missing or invalid parameters",
                },
                {
                  code: "401",
                  meaning: "Unauthorised: invalid or missing API key",
                },
                {
                  code: "403",
                  meaning:
                    "Forbidden: no integration found for this candidate",
                },
                {
                  code: "404",
                  meaning:
                    "Not found: candidate profile not found or not opted in",
                },
                {
                  code: "429",
                  meaning: "Rate limit exceeded: slow down requests",
                },
                {
                  code: "500",
                  meaning: "Server error: contact support@joborigo.com",
                },
              ].map((e) => (
                <div
                  key={e.code}
                  className="flex items-start gap-4 p-3 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <span className="text-sm font-mono font-bold text-gray-700 dark:text-gray-300 w-10 flex-shrink-0">
                    {e.code}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {e.meaning}
                  </span>
                </div>
              ))}
            </div>
          </Section>

          <Section id="rate-limits" title="Rate limits">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Current limits per API key:
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  endpoint: "/arena/candidates",
                  limit: "60 requests / minute",
                },
                {
                  endpoint: "/arena/job-positions",
                  limit: "60 requests / minute",
                },
                {
                  endpoint: "/employer/connected-candidates",
                  limit: "60 requests / minute",
                },
              ].map((r) => (
                <div
                  key={r.endpoint}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <code className="text-sm font-mono text-brand-violet dark:text-brand-violet-on-dark">
                    {r.endpoint}
                  </code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {r.limit}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Need higher limits? Contact support@joborigo.com with your use
              case.
            </p>
          </Section>

          <div className="rounded-2xl bg-brand-violet p-px">
            <div className="rounded-2xl bg-white dark:bg-brand-dark-surface px-8 py-8 text-center">
              <h2 className="text-xl font-bold mb-2">
                Ready to start sourcing?
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
                Create a free employer account and start browsing anonymous
                candidates today.
              </p>
              <Link
                to="/employers/register"
                className="inline-block bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
              >
                Get started
              </Link>
              <p className="text-xs text-gray-400 mt-3">
                Questions?{" "}
                <a
                  href="mailto:support@joborigo.com"
                  className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  support@joborigo.com
                </a>
              </p>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
